import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token to every request
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('mk_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 → redirect to login
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      sessionStorage.removeItem('mk_token');
      sessionStorage.removeItem('mk_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────────
export const login = (username, password) =>
  api.post('/auth/login', { username, password });

export const changePassword = (userId, newPassword) =>
  api.post('/auth/change-password', { userId, newPassword });

// ── Students ──────────────────────────────────────────────────────
export const getStudents = () => api.get('/students');
export const getStudent = nr => api.get(`/students/${nr}`);
export const createStudent = data => api.post('/students', data);
export const updateStudent = (nr, data) => api.put(`/students/${nr}`, data);
export const deactivateStudent = (nr, data) => api.post(`/students/${nr}/deactivate`, data);
export const reactivateStudent = nr => api.post(`/students/${nr}/reactivate`);
export const kurswechsel = (nr, data) => api.post(`/students/${nr}/kurswechsel`, data);
export const jahreswechsel = data => api.post('/students/jahreswechsel', data);
export const setStudentFamily = (nr, familyId) => api.patch(`/students/${nr}/family`, { familyId });

// ── Waiting ───────────────────────────────────────────────────────
export const getWaiting = () => api.get('/waiting');
export const createWaiting = data => api.post('/waiting', data);
export const updateWaiting = (nr, data) => api.put(`/waiting/${nr}`, data);
export const deleteWaiting = nr => api.delete(`/waiting/${nr}`);

// ── Payments ──────────────────────────────────────────────────────
export const getPayments = season => api.get('/payments', { params: { season } });
export const getPayment = (studentNr, season) => api.get(`/payments/${studentNr}/${season}`);
export const savePayment = data => api.post('/payments', data);
export const updateMonth = (studentNr, season, monthIndex, amount, date) =>
  api.patch(`/payments/${studentNr}/${season}/month`, { monthIndex, amount, date });

// ── Families ──────────────────────────────────────────────────────
export const getFamilies = () => api.get('/families');
export const createFamily = data => api.post('/families', data);
export const updateFamily = (id, data) => api.put(`/families/${id}`, data);
export const deleteFamily = id => api.delete(`/families/${id}`);
export const jrsConfirm = id => api.post(`/families/${id}/jrs-confirm`);
export const getJrsStatus = () => api.get('/families/jrs/status');
export const initiateJrs = () => api.post('/families/jrs/initiate');

// ── Seasons ───────────────────────────────────────────────────────
export const getSeasons = () => api.get('/seasons');
export const createSeason = data => api.post('/seasons', data);
export const activateSeason = code => api.put(`/seasons/${code}/activate`);
export const seasonRollover = data => api.post('/seasons/rollover', data);

// ── BuT ───────────────────────────────────────────────────────────
export const getBut = () => api.get('/but');
export const createBut = data => api.post('/but', data);
export const updateBut = (nr, data) => api.put(`/but/${nr}`, data);
export const getButPayments = () => api.get('/but/payments');
export const createButPayment = data => api.post('/but/payments', data);
export const updateButPayment = (id, data) => api.put(`/but/payments/${id}`, data);
export const markButPaymentReceived = id => api.patch(`/but/payments/${id}/received`);
export const deleteBut = nr => api.delete(`/but/${nr}`);

// ── Users ─────────────────────────────────────────────────────────
export const getUsers = () => api.get('/users');
export const createUser = data => api.post('/users', data);
export const changeUserPassword = (id, password) => api.patch(`/users/${id}/password`, { password });
export const deleteUser = id => api.delete(`/users/${id}`);

// ── Config ────────────────────────────────────────────────────────
export const getConfig = () => api.get('/config');
export const addConfigValue = data => api.post('/config', data);
export const updateConfigValue = (id, data) => api.patch(`/config/${id}`, data);
export const deleteConfigValue = id => api.delete(`/config/${id}`);

// ── Reports ───────────────────────────────────────────────────────
export const getStats = season => api.get(`/reports/stats/${season}`);
export const getBalances = season => api.get(`/reports/balances/${season}`);

// ── Reports (extended) ───────────────────────────────────────────
export const getRoster = gruppe => api.get('/reports/roster', { params: gruppe ? { gruppe } : {} });
export const getButReport = () => api.get('/reports/but');

// ── Config: counters ──────────────────────────────────────────────
export const getCounters = () => api.get('/config/counters');
export const updateCounter = (key, data) => api.put(`/config/counters/${key}`, data);

// ── Export / Backup ───────────────────────────────────────────────
export const exportData = (type, season) =>
  api.get(`/export/${type}`, { params: { season }, responseType: 'blob' });

export const exportBackup = () =>
  api.get('/export/backup', { responseType: 'blob' });

export const importBackup = formData =>
  api.post('/import/backup', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

export default api;
